/**
 * VolumeControls
 *
 * Popover triggered from the Navbar speaker icon. Houses three controls:
 *   - Mute toggle (existing behavior — silences EVERYTHING).
 *   - SFX volume slider (0..100).
 *   - Music volume slider (0..100).
 *
 * Volumes persist via `localStorage` keys `cwb_sfx_vol` + `cwb_music_vol`
 * (handled inside `lib/sound.js`). The Navbar still owns the click-out
 * handler; this component just renders the open panel.
 */
import { Volume2, VolumeX, Music, Activity, Headphones } from "lucide-react";
import { useSound } from "../lib/sound";

function VolumeSlider({ label, value, onChange, icon: Icon, tone, testId }) {
  const pct = Math.round((value || 0) * 100);
  return (
    <div className="flex items-center gap-3" data-testid={`volume-row-${testId}`}>
      <div
        className="w-7 h-7 flex items-center justify-center border flex-shrink-0"
        style={{ borderColor: `${tone}66`, color: tone }}
      >
        <Icon className="w-3.5 h-3.5" />
      </div>
      <div className="flex-1 min-w-0">
        <div className="flex items-center justify-between mb-1">
          <span className="font-mono-cosmic text-[9px] tracking-[0.3em] uppercase text-white/60">
            {label}
          </span>
          <span
            className="font-mono-cosmic text-[10px] tabular-nums"
            style={{ color: tone }}
            data-testid={`volume-pct-${testId}`}
          >
            {pct}%
          </span>
        </div>
        <input
          type="range"
          min={0}
          max={100}
          value={pct}
          onChange={(e) => onChange(Number(e.target.value) / 100)}
          className="w-full cursor-pointer accent-[#00ffff]"
          style={{ accentColor: tone }}
          data-testid={`volume-slider-${testId}`}
          aria-label={label}
        />
      </div>
    </div>
  );
}

export default function VolumeControls({ onClose, hint = false }) {
  const { muted, toggleMuted, sfxVol, musicVol, setSfxVol, setMusicVol } = useSound();
  return (
    <div
      className="fixed top-[68px] right-3 sm:right-6 w-72 sm:w-80 border border-[#00ffff]/40 p-4 z-[60] live-notif-pop"
      style={{
        boxShadow: "0 8px 32px rgba(0,0,0,0.6), 0 0 18px rgba(0,255,255,0.18)",
        background: "rgba(5,5,12,0.96)",
        backdropFilter: "blur(18px)",
        WebkitBackdropFilter: "blur(18px)",
      }}
      data-testid="volume-controls-panel"
      role="dialog"
      aria-label="Audio settings"
    >
      <div className="flex items-center justify-between mb-3">
        <div className="font-mono-cosmic text-[10px] tracking-[0.35em] text-[#00ffff] uppercase">
          // AUDIO
        </div>
        <button
          type="button"
          onClick={() => { toggleMuted(); }}
          className="font-mono-cosmic text-[10px] tracking-[0.25em] uppercase border px-2.5 py-1 inline-flex items-center gap-1.5 transition"
          style={{
            borderColor: muted ? "#ff007f80" : "#00ffff80",
            color: muted ? "#ff007f" : "#00ffff",
          }}
          data-testid="volume-mute-toggle"
        >
          {muted ? <VolumeX className="w-3.5 h-3.5" /> : <Volume2 className="w-3.5 h-3.5" />}
          {muted ? "Muted" : "Live"}
        </button>
      </div>
      <div className={`space-y-4 ${muted ? "opacity-40 pointer-events-none" : ""}`}>
        <VolumeSlider
          label="SFX"
          value={sfxVol}
          onChange={setSfxVol}
          icon={Activity}
          tone="#00ff41"
          testId="sfx"
        />
        <VolumeSlider
          label="Music"
          value={musicVol}
          onChange={setMusicVol}
          icon={Music}
          tone="#b026ff"
          testId="music"
        />
      </div>
      <div
        className="mt-4 pt-3 border-t border-white/10 font-mono-cosmic text-[9px] tracking-[0.25em] uppercase"
        data-testid="volume-footer-hint"
      >
        {hint ? (
          <span className="inline-flex items-center gap-1.5 text-[#ffd700]">
            <Headphones className="w-3 h-3" />
            // HEADPHONES RECOMMENDED · PER-SECTOR MUSIC LIVE
          </span>
        ) : (
          <span className="text-white/40">
            Per-sector music · Beginner → Legendary
          </span>
        )}
      </div>
      {onClose && (
        <button
          type="button"
          onClick={onClose}
          className="absolute -top-2 -right-2 w-5 h-5 flex items-center justify-center bg-black border border-white/25 text-white/60 hover:text-white hover:border-white text-xs"
          data-testid="volume-controls-close"
          aria-label="Close audio settings"
        >
          ×
        </button>
      )}
    </div>
  );
}
